import React from 'react';
import { Table, Button, Badge, ProgressBar } from 'react-bootstrap';
import moment from 'moment';

export const GoalTable = ({ goals, onDelete, onEdit, onUpdateProgress }) => {
    const getStatusBadge = (status) => {
        const statusConfig = {
            achieved: {
                bg: 'success',
                icon: 'ri-check-double-line',
                label: 'Achieved'
            },
            'in-progress': {
                bg: 'primary',
                icon: 'ri-time-line',
                label: 'In Progress'
            },
            upcoming: {
                bg: 'info',
                icon: 'ri-calendar-line',
                label: 'Upcoming'
            }
        };
        const config = statusConfig[status] || statusConfig['in-progress'];
        return (
            <Badge bg={config.bg} className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                <i className={config.icon}></i>
                <span>{config.label}</span>
            </Badge>
        );
    };

    const getPriorityBadge = (priority) => {
        const priorityConfig = {
            high: {
                bg: 'danger',
                icon: 'ri-arrow-up-line',
                label: 'High'
            },
            medium: {
                bg: 'warning',
                icon: 'ri-drag-move-line',
                label: 'Medium'
            },
            low: {
                bg: 'info',
                icon: 'ri-arrow-down-line',
                label: 'Low'
            }
        };
        const config = priorityConfig[priority];
        return (
            <Badge bg={config.bg} className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                <i className={config.icon}></i>
                <span>{config.label}</span>
            </Badge>
        );
    };

    const getCategoryBadge = (category) => {
        const categoryConfig = {
            savings: { bg: 'primary', icon: 'ri-money-dollar-box-line' },
            investment: { bg: 'success', icon: 'ri-stock-line' },
            debt: { bg: 'danger', icon: 'ri-bank-card-line' },
            purchase: { bg: 'info', icon: 'ri-shopping-cart-line' },
            education: { bg: 'warning', icon: 'ri-book-open-line' },
            retirement: { bg: 'secondary', icon: 'ri-hand-coin-line' },
            other: { bg: 'dark', icon: 'ri-folder-line' }
        };
        const config = categoryConfig[category] || categoryConfig.other;
        return (
            <Badge bg={config.bg} className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                <i className={config.icon}></i>
                <span>{category.charAt(0).toUpperCase() + category.slice(1)}</span>
            </Badge>
        );
    };

    const calculateProgress = (current, target) => {
        return (current / target) * 100;
    };

    const getDaysRemaining = (targetDate) => {
        const days = moment(targetDate).diff(moment(), 'days');
        return days >= 0 ? days : 0;
    };

    const getProgressBarVariant = (progress) => {
        if (progress >= 100) return 'success';
        if (progress >= 75) return 'primary';
        if (progress >= 50) return 'info';
        if (progress >= 25) return 'warning';
        return 'danger';
    };

    return (
        <Table responsive hover className="table-striped">
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Progress</th>
                    <th>Target Date</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {goals.map((goal) => {
                    const progress = calculateProgress(goal.currentAmount, goal.targetAmount);
                    const daysRemaining = getDaysRemaining(goal.targetDate);
                    
                    return (
                        <tr key={goal.id}>
                            <td>
                                <div className="fw-medium">{goal.name}</div>
                                <small className="text-muted">{goal.description}</small>
                            </td>
                            <td>{getCategoryBadge(goal.category)}</td>
                            <td style={{ minWidth: '200px' }}>
                                <div className="d-flex justify-content-between mb-1">
                                    <small className="fw-medium">
                                        ${goal.currentAmount.toLocaleString()} of ${goal.targetAmount.toLocaleString()}
                                    </small>
                                    <small className={`fw-medium text-${getProgressBarVariant(progress)}`}>
                                        {progress.toFixed(1)}%
                                    </small>
                                </div>
                                <ProgressBar 
                                    now={progress} 
                                    variant={getProgressBarVariant(progress)}
                                    style={{ height: '6px' }}
                                />
                                <small className="text-muted d-block mt-1">
                                    <i className="ri-time-line me-1"></i>
                                    {daysRemaining} days remaining
                                </small>
                            </td>
                            <td>
                                <div className="fw-medium">{moment(goal.targetDate).format('MMM DD, YYYY')}</div>
                                <small className="text-muted">
                                    Started: {moment(goal.startDate).format('MMM DD, YYYY')}
                                </small>
                            </td>
                            <td>{getPriorityBadge(goal.priority)}</td>
                            <td>{getStatusBadge(goal.status)}</td>
                            <td>
                                <div className="d-flex gap-2">
                                    {goal.status !== 'achieved' && (
                                        <>
                                            <Button 
                                                variant="outline-success" 
                                                size="sm"
                                                className="btn-icon"
                                                title="Update Progress"
                                                onClick={() => onUpdateProgress(goal.id)}
                                            >
                                                <i className="ri-add-circle-line"></i>
                                            </Button>
                                            <Button 
                                                variant="outline-primary" 
                                                size="sm"
                                                className="btn-icon"
                                                title="Edit Goal"
                                                onClick={() => onEdit(goal)}
                                            >
                                                <i className="ri-pencil-line"></i>
                                            </Button>
                                        </>
                                    )}
                                    <Button 
                                        variant="outline-danger" 
                                        size="sm"
                                        className="btn-icon"
                                        title="Delete Goal"
                                        onClick={() => onDelete(goal.id)}
                                    >
                                        <i className="ri-delete-bin-line"></i>
                                    </Button>
                                </div>
                            </td>
                        </tr>
                    );
                })}
                {goals.length === 0 && (
                    <tr>
                        <td colSpan="7" className="text-center py-4">
                            <i className="ri-inbox-line fs-24 text-muted mb-2"></i>
                            <p className="text-muted mb-0">No goals found</p>
                        </td>
                    </tr>
                )}
            </tbody>
        </Table>
    );
}; 